import type { DropSectionComponents, DropSections } from './types';

// One lazy import per component, so each lands in a chunk of its own and a
// drop page fetches only the ones its sections render.
const componentImports: {
	[Name in keyof DropSectionComponents]: () => Promise<
		DropSectionComponents[Name]
	>;
} = {
	DropAnchor: () =>
		import('../../components/DropAnchor').then((m) => m.DropAnchor),
	DropButton: () =>
		import('../../components/DropButton').then((m) => m.DropButton),
	DropCarousel: () =>
		import('../../components/DropCarousel').then((m) => m.DropCarousel),
	DropComponentBackground: () =>
		import('../../components/DropComponentBackground').then(
			(m) => m.DropComponentBackground
		),
	DropCountdown: () =>
		import('../../components/DropCountdown').then((m) => m.DropCountdown),
	DropCountdownButtons: () =>
		import('../../components/DropCountdownButtons').then(
			(m) => m.DropCountdownButtons
		),
	DropFaqSection: () =>
		import('../../components/DropFaqSection').then((m) => m.DropFaqSection),
	DropGallery: () =>
		import('../../components/DropGallery').then((m) => m.DropGallery),
	DropGfgArtwork: () =>
		import('../../components/DropGfgArtwork').then((m) => m.DropGfgArtwork),
	DropGfgPatches: () =>
		import('../../components/DropGfgPatches').then((m) => m.DropGfgPatches),
	DropHero: () => import('../../components/DropHero').then((m) => m.DropHero),
	DropHybridFormats: () =>
		import('../../components/DropHybridFormats').then(
			(m) => m.DropHybridFormats
		),
	DropHybridProductCardGrid: () =>
		import('../../components/DropHybridProductCardGrid').then(
			(m) => m.DropHybridProductCardGrid
		),
	DropImage: () =>
		import('../../components/DropImage').then((m) => m.DropImage),
	DropImageCarousel: () =>
		import('../../components/DropImageCarousel').then(
			(m) => m.DropImageCarousel
		),
	DropLogos: () =>
		import('../../components/DropLogos').then((m) => m.DropLogos),
	DropMadcCarousel: () =>
		import('../../components/DropMadcCarousel').then((m) => m.DropMadcCarousel),
	DropNewsletter: () =>
		import('../../components/DropNewsletter').then((m) => m.DropNewsletter),
	DropNftVideo: () =>
		import('../../components/DropNftVideo').then((m) => m.DropNftVideo),
	DropPlatform: () =>
		import('../../components/DropPlatform').then((m) => m.DropPlatform),
	DropProductCardGrid: () =>
		import('../../components/DropProductCardGrid').then(
			(m) => m.DropProductCardGrid
		),
	DropProductCards: () =>
		import('../../components/DropProductCards').then((m) => m.DropProductCards),
	DropQuote: () =>
		import('../../components/DropQuote').then((m) => m.DropQuote),
	DropSlideshow: () =>
		import('../../components/DropSlideshow').then((m) => m.DropSlideshow),
	DropSpinsAlgorithmInformation: () =>
		import('../../components/DropSpinsAlgorithmInformation').then(
			(m) => m.DropSpinsAlgorithmInformation
		),
	DropSpinsFormat: () =>
		import('../../components/DropSpinsFormat').then((m) => m.DropSpinsFormat),
	DropSpinsStyles: () =>
		import('../../components/DropSpinsStyles').then((m) => m.DropSpinsStyles),
	DropTabBar: () =>
		import('../../components/DropTabBar').then((m) => m.DropTabBar),
	DropTopBarEnd: () =>
		import('../../components/DropTabBarEnd').then((m) => m.DropTopBarEnd),
	DropTextSection: () =>
		import('../../components/DropTextSection').then((m) => m.DropTextSection),
	DropTheCurrencyAntiCounterfeinting: () =>
		import('../../components/DropTheCurrencyAntiCounterfeinting').then(
			(m) => m.DropTheCurrencyAntiCounterfeinting
		),
	DropTimeline: () =>
		import('../../components/DropTimeline').then((m) => m.DropTimeline),
	DropVideo: () =>
		import('../../components/DropVideo').then((m) => m.DropVideo),
	DropWhatsappButton: () =>
		import('../../components/DropWhatsappButton').then(
			(m) => m.DropWhatsappButton
		),
	RegisterInterestButtons: () =>
		import('#lib/websites/register-interest/components/RegisterInterestButtons/index.js').then(
			(m) => m.RegisterInterestButtons
		),
};

const importComponents = async (
	...names: (keyof DropSectionComponents)[]
): Promise<DropSections> => {
	const components = await Promise.all(
		names.map((name) => componentImports[name]())
	);

	return Object.fromEntries(
		names.map((name, index) => [name, components[index]])
	);
};

// Each section key (`getDropSectionKey`) to the components its branch of
// `DropPageGenerator` renders. Keep the two in step: a section added to the
// generator needs its entry here, or it renders nothing.
export const dropSectionImports = {
	Drop_Anchor: () => importComponents('DropAnchor'),
	Drop_Button: () => importComponents('DropButton', 'RegisterInterestButtons'),
	Drop_Carousel: () => importComponents('DropCarousel'),
	Drop_Component_Background: () => importComponents('DropComponentBackground'),
	Drop_Countdown: () =>
		importComponents('DropCountdown', 'RegisterInterestButtons'),
	Drop_Countdown_No_Button: () =>
		importComponents('DropCountdown', 'RegisterInterestButtons'),
	Drop_FAQ_Section: () => importComponents('DropFaqSection'),
	Drop_Gallery: () => importComponents('DropGallery'),
	// The hero also renders the mobile countdown, or the countdown buttons
	// when the drop has no countdown section.
	Drop_Hero: () =>
		importComponents('DropHero', 'DropCountdown', 'DropCountdownButtons'),
	Drop_Hybrid_Formats: () => importComponents('DropHybridFormats'),
	Drop_Hybrid_Product_Card_Grid: () =>
		importComponents('DropHybridProductCardGrid'),
	Drop_Image: () => importComponents('DropImage'),
	Drop_Image_Carousel: () => importComponents('DropImageCarousel'),
	Drop_Logos: () => importComponents('DropLogos'),
	Drop_Newsletter: () => importComponents('DropNewsletter'),
	Drop_Platform: () => importComponents('DropPlatform'),
	Drop_Product_Card_Grid: () => importComponents('DropProductCardGrid'),
	Drop_Product_Cards: () => importComponents('DropProductCards'),
	Drop_Quote: () => importComponents('DropQuote'),
	Drop_Slideshow: () => importComponents('DropSlideshow'),
	Drop_Tab_Bar: () => importComponents('DropTabBar'),
	Drop_Tab_Bar_End: () => importComponents('DropTopBarEnd'),
	Drop_Text_Section: () => importComponents('DropTextSection'),
	Drop_Timeline: () => importComponents('DropTimeline'),
	Drop_Video: () => importComponents('DropVideo'),
	// A WhatsApp button paired with the button before it renders that one
	// too, which the Drop_Button entry already loads.
	Drop_Whatsapp_Button: () => importComponents('DropWhatsappButton'),
	'Drop_Custom_Component:Anti_Counterfeiting_Interactive_Component': () =>
		importComponents('DropTheCurrencyAntiCounterfeinting'),
	'Drop_Custom_Component:Drop_Empresses_NFT_Video': () =>
		importComponents('DropNftVideo'),
	'Drop_Custom_Component:Drop_GFG_Artwork': () =>
		importComponents('DropGfgArtwork'),
	'Drop_Custom_Component:GFG_Patches_And_Sections': () =>
		importComponents('DropGfgPatches'),
	'Drop_Custom_Component:MadC_Carousel_With_Works': () =>
		importComponents('DropMadcCarousel'),
	'Drop_Custom_Component:Spins_Algorithm_Information': () =>
		importComponents('DropSpinsAlgorithmInformation'),
	// The format block renders a text section above it.
	'Drop_Custom_Component:Spins_Format': () =>
		importComponents('DropTextSection', 'DropSpinsFormat'),
	'Drop_Custom_Component:Spins_Styles': () =>
		importComponents('DropSpinsStyles'),
} satisfies Record<string, () => Promise<DropSections>>;

export type DropSectionKey = keyof typeof dropSectionImports;
